import katex from 'katex';
export const parseTooltip = (rawHtml, options = {}) => {
  const {
    maxChars = null,
    includeKatex = true,
    fontSize,
    cleanAttributesList = ['font-size', 'fontsize', 'class'],
    inverted = false,
    extraClasses = [],
  } = options;
  const container = parseHtmlToElement(rawHtml);
  cleanAttributes(container, cleanAttributesList);
  normalizeFontSizes(container, fontSize, inverted);

  renderKatex(container, fontSize, inverted);
  if (typeof maxChars === 'number') {
    return truncateHtml(container, maxChars, includeKatex);
  }

  normalizeClasses(container, extraClasses);

  return container.innerHTML;
};
// 🔧 Parse raw HTML string into a DOM element
function parseHtmlToElement(html) {
  const container = document.createElement('div');
  container.innerHTML = html;
  return container;
}
// 🔧 Remove unwanted attributes like class, font-size, fontsize
function cleanAttributes(
  container,
  cleanAttributesList = ['font-size', 'fontsize', 'class'],
) {
  container.querySelectorAll('*').forEach(el => {
    [...el.attributes].forEach(attr => {
      if (cleanAttributesList.includes(attr.name.toLowerCase())) {
        el.removeAttribute(attr.name);
      }
    });
  });
}
function normalizeFontSizes(container, fontSize, inverted) {
  const extra = inverted ? 'color: white; ' : '';

  container.querySelectorAll('[style]').forEach(el => {
    const style = el.getAttribute('style');
    let updatedStyle = style;

    if (fontSize && style?.includes('font-size')) {
      updatedStyle
        .replace(/font-size\s*:\s*[^;]+;?/gi, '')
        .trim()
        .replace(/;$/, '');
    }

    el.setAttribute(
      'style',
      `${updatedStyle}; ${extra}font-size: ${fontSize} !important`,
    );
  });
}
function normalizeClasses(container, classes) {
  container.querySelectorAll(':scope > *').forEach(el => {
    el.setAttribute('class', classes.join(' '));
  });
}
// 🔧 Render [data-katex] spans using KaTeX
function renderKatex(container, fontSize, inverted) {
  container.querySelectorAll('[data-katex]').forEach(span => {
    let latex = span.textContent?.trim();
    if (latex.startsWith('$$') && latex.endsWith('$$')) {
      latex = latex.slice(2, -2).trim();
    } else if (latex.startsWith('$') && latex.endsWith('$')) {
      latex = latex.slice(1, -1).trim();
    }
    const rendered = document.createElement('span');
    rendered.classList.add('rendered-katex'); // ✅ This is crucial
    rendered.setAttribute('dir', 'ltr');
    if (fontSize) rendered.style.fontSize = fontSize;
    if (inverted) rendered.style.color = 'white';
    try {
      katex.render(latex, rendered, {
        throwOnError: false,
        displayMode: false,
      });
      span.replaceWith(rendered);
    } catch (err) {
      console.error('KaTeX error:', err);
      span.textContent = '[Invalid KaTeX]';
    }
  });
}
function truncateHtml(container, maxUnits, includeKatex) {
  let count = 0;
  let done = false;
  let addedEllipsis = false;

  const countLatexFunctions = latex => {
    return (latex.match(/\\[a-zA-Z]+/g) || []).length;
  };

  const truncateNode = node => {
    if (done) return null;

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (count + text.length <= maxUnits) {
        count += text.length;
        return document.createTextNode(text);
      } else {
        const remaining = maxUnits - count;
        count = maxUnits;
        done = true;
        addedEllipsis = true;
        return document.createTextNode(text.slice(0, remaining) + '...');
      }
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const isKatex = node.classList.contains('rendered-katex');

      if (isKatex) {
        const latex =
          node.querySelector('annotation[encoding="application/x-tex"]')
            ?.textContent || '';
        const functionCount = countLatexFunctions(latex);
        if (!includeKatex) {
          return node.cloneNode(true);
        }

        if (count + functionCount <= maxUnits) {
          count += functionCount;
          return node.cloneNode(true);
        } else {
          done = true;
          return null;
        }
      }

      const clone = node.cloneNode(false);
      for (const child of node.childNodes) {
        const newChild = truncateNode(child);
        if (newChild) clone.appendChild(newChild);
        if (done) break;
      }

      return clone;
    }

    return null;
  };

  const truncated = truncateNode(container);
  const wrapper = document.createElement('div');
  if (truncated) wrapper.appendChild(truncated);

  // If truncation happened, and no "..." was added (like when a KaTeX block was dropped)
  if (done && !addedEllipsis) {
    wrapper.appendChild(document.createTextNode('...'));
  }

  return wrapper.outerHTML;
}

export function isHTML(input: string): boolean {
  const doc = new DOMParser().parseFromString(input, 'text/html');
  const hasElementNodes = Array.from(doc.body.childNodes).some(
    node => node.nodeType === 1, // ELEMENT_NODE
  );
  return hasElementNodes;
}

export function countUnitsInHtml(rawHtml, { includeKatex = true } = {}) {
  let count = 0;
  const container = parseHtmlToElement(rawHtml);
  const countLatexFunctions = latex => {
    return latex.match(/\\[a-zA-Z]+|[a-zA-Z]/g)?.length || 0;
  };

  const traverse = node => {
    if (node.nodeType === Node.TEXT_NODE) {
      count += (node.textContent || '').length;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const isKatex = node.classList.contains('rendered-katex');
      if (isKatex) {
        const latex =
          node.querySelector('annotation[encoding="application/x-tex"]')
            ?.textContent || '';
        if (includeKatex) {
          count += countLatexFunctions(latex);
        }
        return; // don't traverse inside KaTeX
      }

      for (const child of node.childNodes) {
        traverse(child);
      }
    }
  };

  traverse(container);
  return count;
}
